// lib/image-postprocess.mjs のブラウザ代替。デコード/エンコードだけを Canvas
// （createImageBitmap / OffscreenCanvas）で行い、**透過化アルゴリズム本体は
// lib/image-postprocess-core.mjs（Node 版と同一実装）を共有**する。
// ※ 独自実装は禁止: 2026-07-11 に隣接連鎖式の独自フラッドフィルがグラデーションを伝って
//   キャラ本体まで透過する不具合を起こした。ロジック差分はコア共有で構造的に防ぐ。
// 戻り値の形は Node 版と同一（async な点のみ異なり、共有側は await 呼び出しで両対応）:
//   { buffer, contentType, mode: 'native'|'removed'|'unsupported', removedRatio }
import { hasMeaningfulAlpha, removeConnectedBackground, clearPortraitFootArtifacts } from '../../lib/image-postprocess-core.mjs';

async function decodeToImageData(buf, contentType) {
    try {
        const bitmap = await createImageBitmap(new Blob([buf], { type: contentType }));
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();
        return { canvas, ctx, image: ctx.getImageData(0, 0, canvas.width, canvas.height) };
    } catch {
        return null;
    }
}

export async function toTransparentPng(buf, contentType, { tolerance = 32 } = {}) {
    const decoded = await decodeToImageData(buf, contentType);
    if (!decoded) return { buffer: buf, contentType, mode: 'unsupported', removedRatio: null };
    const rgba = { width: decoded.image.width, height: decoded.image.height, data: decoded.image.data };
    if (contentType === 'image/png' && hasMeaningfulAlpha(rgba)) {
        return { buffer: buf, contentType: 'image/png', mode: 'native', removedRatio: null };
    }
    const { removedRatio } = removeConnectedBackground(rgba, { tolerance });
    decoded.ctx.putImageData(decoded.image, 0, 0);
    const blob = await decoded.canvas.convertToBlob({ type: 'image/png' });
    const out = new Uint8Array(await blob.arrayBuffer());
    return { buffer: out, contentType: 'image/png', mode: 'removed', removedRatio };
}

/** Node 版 clearFootArtifacts と同一仕様（Canvas デコード・アルゴリズムはコア共有）。 */
export async function clearFootArtifacts(buf, contentType, { tolerance = 32, footRegionRatio, shadowChromaMax, shadowMinBrightness } = {}) {
    const decoded = await decodeToImageData(buf, contentType);
    if (!decoded) return { buffer: buf, contentType, mode: 'unsupported', removedRatio: null, backgroundRemoved: false };
    const rgba = { width: decoded.image.width, height: decoded.image.height, data: decoded.image.data };
    const backgroundRemoved = !(contentType === 'image/png' && hasMeaningfulAlpha(rgba));
    if (backgroundRemoved) removeConnectedBackground(rgba, { tolerance });
    const { removedRatio } = clearPortraitFootArtifacts(rgba, { tolerance, footRegionRatio, shadowChromaMax, shadowMinBrightness });
    decoded.ctx.putImageData(decoded.image, 0, 0);
    const blob = await decoded.canvas.convertToBlob({ type: 'image/png' });
    const out = new Uint8Array(await blob.arrayBuffer());
    return { buffer: out, contentType: 'image/png', mode: 'refined', removedRatio, backgroundRemoved };
}
